import { Box, CircularProgress, FormHelperText, Typography } from "@mui/material";
import { DescriptionOutlined } from "@mui/icons-material";
import FileDropSection from "components/FileDropSection";
import useDirectUpload from "hooks/useDirectUpload";
import { useAlert } from "hooks/alerts/useAlert";
import { useState } from "react";
import { ActiveStorageAttachment } from "types/activeStorageAttachment";
import { coiStoredOnLocation } from "./coiRequirement";
import { useMissingRequirement } from "./requirementError";

interface CertificateOfInsuranceUploadProps {
  option: string | null;
  locationFile?: ActiveStorageAttachment | string | null;
  pickupFile?: ActiveStorageAttachment | null;
  onUpload: (signedId: string) => Promise<void>;
  onDelete: () => Promise<void>;
}

function savedCertificateFile(
  file: ActiveStorageAttachment | string | null | undefined
): ActiveStorageAttachment | undefined {
  if (!file || typeof file === "string" || !file.url) {
    return undefined;
  }

  return file;
}

export default function CertificateOfInsuranceUpload({
  option,
  locationFile,
  pickupFile,
  onUpload,
  onDelete,
}: CertificateOfInsuranceUploadProps) {
  const { uploadAsync } = useDirectUpload({
    keyPrefix: "/pickup/certificate_of_insurance/",
    service: "amazon_public",
  });
  const { alertError } = useAlert();
  const [uploading, setUploading] = useState(false);
  const onRecord = coiStoredOnLocation(option);
  const currentFile = savedCertificateFile(onRecord ? locationFile : pickupFile);
  const certificateMissing = useMissingRequirement("schedule-pickup-site-information") && !currentFile;

  return (
    <Box id="certificate-of-insurance" sx={{ mt: 3 }}>
      <Typography fontWeight={700}>Certificate of Insurance</Typography>
      <Typography mt={0.5} variant="body2" color="text.secondary">
        {onRecord
          ? "Upload the certificate kept on file for this location. The same document is used for every pickup."
          : "This location requires a new certificate for each pickup. Upload the certificate for this pickup."}
      </Typography>

      <Box mt={2}>
        {uploading ? (
          <Box sx={{ display: "flex", minHeight: 120, alignItems: "center", justifyContent: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <FileDropSection
            icon={<DescriptionOutlined color="primary" sx={{ fontSize: "32px" }} />}
            acceptHint="pdf, jpg, jpeg, png"
            accept={{
              "application/pdf": [".pdf"],
              "image/png": [".png"],
              "image/jpeg": [".jpg", ".jpeg"],
            }}
            documents={
              currentFile
                ? [
                    {
                      name: currentFile.filename,
                      url: currentFile.url,
                      onDelete() {
                        onDelete();
                      },
                    },
                  ]
                : undefined
            }
            onUpload={async (files) => {
              const file = files[0];
              if (!file) return;

              setUploading(true);
              try {
                const uploaded = await uploadAsync(file);
                await onUpload(uploaded.signed_id);
              } catch {
                alertError("There was an error uploading the certificate of insurance. Please try again.");
              } finally {
                setUploading(false);
              }
            }}
          />
        )}
      </Box>
      {certificateMissing && <FormHelperText error>Required</FormHelperText>}
    </Box>
  );
}
